/**
 * Copyright 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useMemo, useRef } from "react";
import { When } from "react-if";
import { useClickAway, useToggle } from "react-use";
import { Box, Button, SelectButton, Text, theme } from "@nordcloud/gnui";
import { useDateValues } from "~/hooks";
import { DateRange, Period } from "../Charts/CostAnalysis/types";
import { Separator } from "../Styled";
import { DatePreselects } from "./DatePreselects";
import { DateRangeInputs } from "./DateRangeInputs";
import { ButtonWrapper, Content, Wrapper } from "./styles";
import { EstatePeriod } from "./types";

type Props = {
  isActive: boolean;
  onApply: (range?: DateRange) => void;
  label: string;
  period: EstatePeriod | Period;
  onClear: () => void;
  range?: DateRange;
  disabled?: boolean;
  showPreselects?: boolean;
};

export function DateRangeSelector({
  isActive,
  onApply,
  label,
  period,
  onClear,
  range,
  disabled = false,
  showPreselects = false,
}: Props) {
  const [isOpen, toggleIsOpen] = useToggle(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const {
    endDate,
    setEndDate,
    startDate,
    setStartDate,
    previousPeriodSelected,
    setPreviousPeriodSelected,
    handlePrevPeriodClick,
  } = useDateValues(range);

  const isActionDisabled = useMemo(
    () => isButtonDisabled(startDate, endDate, range),
    [startDate, endDate, range]
  );

  useClickAway(wrapper, () => toggleIsOpen(false));

  const handleRangeClick = useCallback(() => {
    if (period !== EstatePeriod.RANGE && period !== Period.RANGE && range) {
      onApply(range);
    } else {
      toggleIsOpen();
    }
  }, [range, period, toggleIsOpen, onApply]);

  const handleApply = useCallback(() => {
    onApply({
      from: startDate ?? "",
      to: endDate ?? "",
    });
    toggleIsOpen();
  }, [startDate, endDate, onApply, toggleIsOpen]);

  const handleClear = () => {
    onClear();
    setStartDate(undefined);
    setEndDate(undefined);
    setPreviousPeriodSelected(undefined);
  };

  return (
    <Wrapper ref={wrapper}>
      <ButtonWrapper>
        <SelectButton
          name="range"
          value="13"
          labelText={label}
          isActive={isActive}
          style={{ whiteSpace: "nowrap", minWidth: "7.5rem" }}
          disabled={disabled}
          onClick={handleRangeClick}
        />
        <When condition={label !== "Date Range"}>
          <Button
            icon="close"
            size="sm"
            severity={isActive ? undefined : "medium"}
            css={{ borderRadius: "unset", border: "none" }}
            onClick={handleClear}
          />
        </When>
      </ButtonWrapper>
      <Content isVisible={isOpen}>
        <Box innerSpacing="spacing03" shadow="shadow04">
          <When condition={showPreselects}>
            <DatePreselects
              periodSelected={previousPeriodSelected}
              handleClick={handlePrevPeriodClick}
            />
          </When>
          <Text weight="bold">Date range</Text>
          <DateRangeInputs
            startDate={startDate ?? ""}
            endDate={endDate ?? ""}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
          />
          <Separator css={{ marginTop: theme.spacing.spacing04 }} />
          <Button
            disabled={isActionDisabled}
            size="md"
            css={{ width: "98%", display: "inline-block" }}
            marginTop={theme.spacing.spacing04}
            onClick={handleApply}
          >
            Apply
          </Button>
        </Box>
      </Content>
    </Wrapper>
  );
}
function isRangeSet(from?: string, to?: string) {
  return from !== undefined && to !== undefined;
}

function isButtonDisabled(from?: string, to?: string, range?: DateRange) {
  if (range) {
    return range.from === from && range?.to === to;
  }

  return !isRangeSet(from, to);
}
